import axios from 'axios';
import { Pathologist, AuthStatus, ServiceLine, PDFGenerationRequest, CustomContent, UserProfile, ContactInfo, ZohoDirectoryUser, UserRole } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add response interceptor to handle session expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if error is due to session expiration (401 Unauthorized)
    if (error.response?.status === 401) {
      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Extended profile response that may include directory data
export interface ExtendedProfileResponse {
  profile: UserProfile;
  contactInfo: ContactInfo;
  directoryUser?: any; // Optional directory data from backend
}

// Auth API
export const authAPI = {
  checkStatus: async (): Promise<AuthStatus> => {
    const response = await api.get('/auth/status');
    return response.data;
  },

  login: (): void => {
    window.location.href = `${API_BASE_URL}/auth/zoho`;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },

  // Get detailed user profile (now includes directory data from backend)
  getProfile: async (refresh = false): Promise<ExtendedProfileResponse> => {
    const response = await api.get(`/auth/profile?refresh=${refresh}`);
    return response.data;
  },

  // Get user contact information
  getContactInfo: async (): Promise<ContactInfo> => {
    const response = await api.get('/auth/contact-info');
    return response.data;
  },

  // Get user info (legacy compatibility)
  getUser: async (): Promise<{ user: { id: string; profile: UserProfile; email: string; fullName: string; mobile: string } }> => {
    const response = await api.get('/auth/user');
    return response.data;
  },

  getUserRole: async (): Promise<UserRole> => {
    const response = await fetch(`${API_BASE_URL}/auth/user-role`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch user role');
    }
    
    return response.json();
  }
};

// Pathologist API
export const pathologistAPI = {
  getByServiceLine: async (serviceLine: ServiceLine): Promise<Pathologist[]> => {
    const response = await api.get(`/api/pathologists/service/${serviceLine}`);
    return response.data;
  },

  getAll: async (): Promise<Pathologist[]> => {
    const response = await api.get('/api/pathologists');
    return response.data;
  },

  getById: async (id: string): Promise<Pathologist> => {
    const response = await api.get(`/api/pathologists/${id}`);
    return response.data;
  }
};

// Licensing API
export interface LicenseRecord {
  id: string;
  physician: string;
  physician_id: string;
  state: string;
  status: string;
}

export const licensingAPI = {
  // Get all active licenses, optionally filtered by state
  getActiveLicenses: async (state?: string): Promise<LicenseRecord[]> => {
    const params = state ? { state } : {};
    const response = await api.get('/api/licensing/active-by-state', { params });
    return response.data;
  },

  // Get unique states from licensing data
  getStates: async (): Promise<string[]> => {
    const response = await api.get('/api/licensing/states');
    return response.data;
  },

  // Get active licenses for a specific physician
  getPhysicianLicenses: async (physicianName: string): Promise<LicenseRecord[]> => {
    const response = await api.get(`/api/licensing/physician/${encodeURIComponent(physicianName)}`);
    return response.data;
  }
};

// PDF API
export const pdfAPI = {
  generate: async (request: PDFGenerationRequest): Promise<Blob> => {
    const response = await api.post('/api/pdf/generate', request, {
      responseType: 'blob'
    });
    return response.data;
  },

  preview: async (request: PDFGenerationRequest): Promise<Blob> => {
    const response = await api.post('/api/pdf/preview', request, {
      responseType: 'blob'
    });
    return response.data;
  },

  getTemplateContent: async (serviceLine?: string): Promise<CustomContent> => {
    const endpoint = serviceLine ? `/api/template-content/${serviceLine}` : '/api/template-content';
    const response = await api.get(endpoint);
    return response.data;
  },

  updateTemplateContent: async (content: CustomContent, serviceLine: string): Promise<void> => {
    await api.put(`/api/template-content/${serviceLine}`, content);
  },

  refreshTemplateCache: async (): Promise<{ success: boolean; message: string; serviceLines: string[] }> => {
    const response = await api.post('/api/pdf/refresh-template-cache');
    return response.data;
  },

  getTemplateCacheStatus: async (): Promise<{ cached: boolean; ageInSeconds: number | null; serviceLines: string[] }> => {
    const response = await api.get('/api/pdf/template-cache-status');
    return response.data;
  }
};

// Utility function to download blob as file
export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Zoho Directory API
export interface DirectoryUserDetailResponse {
  success: boolean;
  user?: ZohoDirectoryUser;
  error?: string;
  source?: string;
  timestamp?: string;
}

export const directoryAPI = {
  // Get current logged-in user's directory details (backend handles webhook/callback)
  getUserDetail: async (): Promise<DirectoryUserDetailResponse> => {
    const response = await api.get('/api/directory/user-detail');
    return response.data;
  },

  // Get specific user by ID (backend handles webhook/callback)
  getUserById: async (userId: string): Promise<DirectoryUserDetailResponse> => {
    const response = await api.get(`/api/directory/users/${userId}`);
    return response.data;
  },

  // Test the directory API connection
  test: async (): Promise<any> => {
    const response = await api.get('/api/directory/test');
    return response.data;
  },

  // Health check
  health: async (): Promise<any> => {
    const response = await api.get('/api/directory/health');
    return response.data;
  }
};

// Role API
export const roleAPI = {
  // Get user role and permissions
  getUserRole: async (): Promise<{
    hasRole: boolean;
    department: string | null;
    canEditTemplate: boolean;
    error?: string;
  }> => {
    const response = await api.get('/auth/user-role');
    return response.data;
  }
};

export default api;